// components/SettingsForm.jsx
import React, { useState, useEffect } from "react";
import { useSettings } from "./useSettings";
import {
  FaGlobe,
  FaImage,
  FaInfoCircle,
  FaList,
  FaStar,
  FaQuoteLeft,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaLinkedin,
  FaSave,
  FaTrash,
  FaUpload,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import CommonBreadcrumb from "../common/CommonBreadcrumb";
const SettingsForm = () => {
  const { settings, imageBaseUrl, isLoading, updateSettings, updateLogo, updateBannerImage, deleteImage, refetch } =
    useSettings();

  const getImageUrl = (imageName) => {
    if (!imageName) return null;
    if (imageName.startsWith("http://") || imageName.startsWith("https://")) return imageName;
    const cleanName = imageName.replace(/^\/?(uploads\/websitesettings\/)?/, "");
    const base = (imageBaseUrl || "https://api.bagdamahasabha.org/uploads/websitesettings/").replace(/\/$/, "");
    return `${base}/${cleanName}`;
  };

  const [formData, setFormData] = useState({
    hero_title: "",
    hero_description: "",
    browse_category_title: "",
    browse_category_sub_title: "",
    featured_venue_title: "",
    featured_venue_sub_title: "",
    testimonial_title: "",
    testimonial_subtitle: "",
    footer_title: "",
    footer_description: "",
    contact_phone: "",
    contact_email: "",
    contact_address: "",
    facebook_link: "",
    instagram_link: "",
    youtube_link: "",
    linkedin_link: "",
    logo: null,
    banner_image: null,
  });

  const [preview, setPreview] = useState({
    logo: null,
    banner_image: null,
  });

  const [activeTab, setActiveTab] = useState("hero");
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData((prev) => ({
        ...prev,
        hero_title: settings.hero_title || "",
        hero_description: settings.hero_description || "",
        browse_category_title: settings.browse_category_title || "",
        browse_category_sub_title: settings.browse_category_sub_title || "",
        featured_venue_title: settings.featured_venue_title || "",
        featured_venue_sub_title: settings.featured_venue_sub_title || "",
        testimonial_title: settings.testimonial_title || "",
        testimonial_subtitle: settings.testimonial_subtitle || "",
        footer_title: settings.footer_title || "",
        footer_description: settings.footer_description || "",
        contact_phone: settings.contact_phone || "",
        contact_email: settings.contact_email || "",
        contact_address: settings.contact_address || "",
        facebook_link: settings.facebook_link || "",
        instagram_link: settings.instagram_link || "",
        youtube_link: settings.youtube_link || "",
        linkedin_link: settings.linkedin_link || "",
      }));
    }
  }, [settings]);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: "", message: "" });
    }, 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showNotification("error", "File size should be less than 5MB");
        return;
      }
      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        showNotification("error", "Please upload a valid image file");
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview((prev) => ({ ...prev, [name]: reader.result }));
      };
      reader.readAsDataURL(file);

      // Instantly upload using dedicated API endpoint
      const uploadData = new FormData();
      uploadData.append(name, file);
      try {
        setIsSubmitting(true);
        if (name === "logo") {
          await updateLogo(uploadData).unwrap();
          showNotification("success", "Logo updated successfully!");
        } else if (name === "banner_image") {
          await updateBannerImage(uploadData).unwrap();
          showNotification("success", "Banner image updated successfully!");
        }
        refetch();
      } catch (err) {
        console.error(`Error updating ${name}:`, err);
        showNotification("error", `Failed to upload ${name}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDeleteImage = async (imageType) => {
    if (window.confirm(`Are you sure you want to delete the ${imageType}?`)) {
      try {
        await deleteImage(imageType).unwrap();
        setPreview((prev) => ({ ...prev, [imageType]: null }));
        showNotification("success", `${imageType} deleted successfully`);
      } catch (error) {
        showNotification("error", "Failed to delete image");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      // Append text fields
      Object.keys(formData).forEach((key) => {
        if (key !== "logo" && key !== "banner_image") {
          if (formData[key]) {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Append files
      if (formData.logo instanceof File) {
        formDataToSend.append("logo", formData.logo);
      }
      if (formData.banner_image instanceof File) {
        formDataToSend.append("banner_image", formData.banner_image);
      }

      await updateSettings(formDataToSend).unwrap();
      showNotification("success", "Settings updated successfully!");
    } catch (error) {
      showNotification("error", "Failed to update settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "hero":
        return renderHeroSection();
      case "categories":
        return renderCategoriesSection();
      case "venues":
        return renderVenuesSection();
      case "testimonials":
        return renderTestimonialsSection();
      case "images":
        return renderImagesSection();
      case "footer":
        return renderFooterSection();
      case "contact":
        return renderContactSection();
      case "social":
        return renderSocialSection();
      default:
        return null;
    }
  };

  const renderHeroSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaInfoCircle className="section-icon" />
        Hero Section
      </h2>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Hero Title</label>
          <input
            type="text"
            name="hero_title"
            value={formData.hero_title}
            onChange={handleInputChange}
            placeholder="Enter hero title"
            className="form-input"
          />
        </div>
        <div className="form-group full-width">
          <label className="form-label">Hero Description</label>
          <textarea
            name="hero_description"
            value={formData.hero_description}
            onChange={handleInputChange}
            placeholder="Enter hero description"
            rows="3"
            className="form-textarea"
          />
        </div>
      </div>
    </div>
  );

  const renderCategoriesSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaList className="section-icon" />
        Categories Section
      </h2>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Category Title</label>
          <input
            type="text"
            name="browse_category_title"
            value={formData.browse_category_title}
            onChange={handleInputChange}
            placeholder="Enter category title"
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Category Sub Title</label>
          <input
            type="text"
            name="browse_category_sub_title"
            value={formData.browse_category_sub_title}
            onChange={handleInputChange}
            placeholder="Enter category sub title"
            className="form-input"
          />
        </div>
      </div>
    </div>
  );

  const renderVenuesSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaStar className="section-icon" />
        Featured Venues
      </h2>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Featured Venue Title</label>
          <input
            type="text"
            name="featured_venue_title"
            value={formData.featured_venue_title}
            onChange={handleInputChange}
            placeholder="Enter featured venue title"
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Featured Venue Sub Title</label>
          <input
            type="text"
            name="featured_venue_sub_title"
            value={formData.featured_venue_sub_title}
            onChange={handleInputChange}
            placeholder="Enter featured venue sub title"
            className="form-input"
          />
        </div>
      </div>
    </div>
  );

  const renderTestimonialsSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaQuoteLeft className="section-icon" />
        Testimonials
      </h2>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Testimonial Title</label>
          <input
            type="text"
            name="testimonial_title"
            value={formData.testimonial_title}
            onChange={handleInputChange}
            placeholder="Enter testimonial title"
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Testimonial Subtitle</label>
          <input
            type="text"
            name="testimonial_subtitle"
            value={formData.testimonial_subtitle}
            onChange={handleInputChange}
            placeholder="Enter testimonial subtitle"
            className="form-input"
          />
        </div>
      </div>
    </div>
  );

  const renderImagesSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaImage className="section-icon" />
        Images
      </h2>
      <div className="image-grid">
        <div className="image-card">
          <div className="image-header">
            <h3>Logo</h3>
            <span className="image-badge">Recommended: 200x200px</span>
          </div>
          <div className="image-preview-container">
            {settings?.logo && !preview.logo && (
              <div className="image-wrapper">
                <img
                  src={getImageUrl(settings.logo)}
                  alt="Current Logo"
                  className="preview-image logo-preview"
                />
                <button
                  type="button"
                  onClick={() => handleDeleteImage("logo")}
                  className="delete-image-btn"
                >
                  <FaTrash />
                </button>
              </div>
            )}
            {preview.logo && (
              <div className="image-wrapper">
                <img
                  src={preview.logo}
                  alt="New Logo Preview"
                  className="preview-image logo-preview"
                />
              </div>
            )}
            {!settings?.logo && !preview.logo && (
              <div className="empty-image-placeholder">
                <FaImage className="placeholder-icon" />
                <p>No logo uploaded</p>
              </div>
            )}
          </div>
          <div className="file-upload-wrapper">
            <label className="file-upload-label">
              <FaUpload className="upload-icon" />
              Choose Logo
              <input
                type="file"
                name="logo"
                accept="image/*"
                onChange={handleFileChange}
                className="file-input"
              />
            </label>
            <p className="file-hint">PNG, JPG, GIF up to 5MB</p>
          </div>
        </div>

        <div className="image-card">
          <div className="image-header">
            <h3>Banner Image</h3>
            <span className="image-badge">Recommended: 1920x600px</span>
          </div>
          <div className="image-preview-container">
            {settings?.banner_image && !preview.banner_image && (
              <div className="image-wrapper">
                <img
                  src={getImageUrl(settings.banner_image)}
                  alt="Current Banner"
                  className="preview-image banner-preview"
                />
                <button
                  type="button"
                  onClick={() => handleDeleteImage("banner_image")}
                  className="delete-image-btn"
                >
                  <FaTrash />
                </button>
              </div>
            )}
            {preview.banner_image && (
              <div className="image-wrapper">
                <img
                  src={preview.banner_image}
                  alt="New Banner Preview"
                  className="preview-image banner-preview"
                />
              </div>
            )}
            {!settings?.banner_image && !preview.banner_image && (
              <div className="empty-image-placeholder">
                <FaImage className="placeholder-icon" />
                <p>No banner uploaded</p>
              </div>
            )}
          </div>
          <div className="file-upload-wrapper">
            <label className="file-upload-label">
              <FaUpload className="upload-icon" />
              Choose Banner
              <input
                type="file"
                name="banner_image"
                accept="image/*"
                onChange={handleFileChange}
                className="file-input"
              />
            </label>
            <p className="file-hint">PNG, JPG, GIF up to 5MB</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFooterSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaGlobe className="section-icon" />
        Footer Section
      </h2>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Footer Title</label>
          <input
            type="text"
            name="footer_title"
            value={formData.footer_title}
            onChange={handleInputChange}
            placeholder="Enter footer title"
            className="form-input"
          />
        </div>
        <div className="form-group full-width">
          <label className="form-label">Footer Description</label>
          <textarea
            name="footer_description"
            value={formData.footer_description}
            onChange={handleInputChange}
            placeholder="Enter footer description"
            rows="3"
            className="form-textarea"
          />
        </div>
      </div>
    </div>
  );

  const renderContactSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaPhone className="section-icon" />
        Contact Information
      </h2>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            <FaPhone className="input-icon" /> Phone
          </label>
          <input
            type="text"
            name="contact_phone"
            value={formData.contact_phone}
            onChange={handleInputChange}
            placeholder="+1 234 567 8900"
            className="form-input with-icon"
          />
        </div>
        <div className="form-group">
          <label className="form-label">
            <FaEnvelope className="input-icon" /> Email
          </label>
          <input
            type="email"
            name="contact_email"
            value={formData.contact_email}
            onChange={handleInputChange}
            placeholder="info@yourcompany.com"
            className="form-input with-icon"
          />
        </div>
        <div className="form-group full-width">
          <label className="form-label">
            <FaMapMarkerAlt className="input-icon" /> Address
          </label>
          <textarea
            name="contact_address"
            value={formData.contact_address}
            onChange={handleInputChange}
            placeholder="123 Main Street, City, Country"
            rows="2"
            className="form-textarea with-icon"
          />
        </div>
      </div>
    </div>
  );

  const renderSocialSection = () => (
    <div className="section-content">
      <h2 className="section-title">
        <FaFacebook className="section-icon" />
        Social Media Links
      </h2>
      <div className="social-grid">
        <div className="social-item facebook">
          <FaFacebook className="social-icon" />
          <input
            type="url"
            name="facebook_link"
            value={formData.facebook_link}
            onChange={handleInputChange}
            placeholder="https://facebook.com/yourpage"
            className="social-input"
          />
        </div>
        <div className="social-item instagram">
          <FaInstagram className="social-icon" />
          <input
            type="url"
            name="instagram_link"
            value={formData.instagram_link}
            onChange={handleInputChange}
            placeholder="https://instagram.com/yourpage"
            className="social-input"
          />
        </div>
        <div className="social-item youtube">
          <FaYoutube className="social-icon" />
          <input
            type="url"
            name="youtube_link"
            value={formData.youtube_link}
            onChange={handleInputChange}
            placeholder="https://youtube.com/yourpage"
            className="social-input"
          />
        </div>
        <div className="social-item linkedin">
          <FaLinkedin className="social-icon" />
          <input
            type="url"
            name="linkedin_link"
            value={formData.linkedin_link}
            onChange={handleInputChange}
            placeholder="https://linkedin.com/yourpage"
            className="social-input"
          />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="loading-container">
        <FaSpinner className="loading-spinner" />
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.type === "success" ? (
            <FaCheckCircle className="notification-icon" />
          ) : (
            <FaTimesCircle className="notification-icon" />
          )}
          <span>{notification.message}</span>
        </div>
      )}

     <CommonBreadcrumb
    title="Website Settings"
    titleIcon={<FaGlobe className="me-2 text-primary" />}
    subtitle="Manage your website content and appearance"
    breadcrumbs={[
        { label: "Home", link: "/admin" },
        { label: "Website Settings" },
    ]}
/>
   <div className="container-fluid">
       <form onSubmit={handleSubmit} className="settings-form">
        <div className="settings-layout">
          <div className="settings-sidebar">
            <div className="tab-list">
              <button
                type="button"
                className={`tab-btn ${activeTab === "hero" ? "active" : ""}`}
                onClick={() => setActiveTab("hero")}
              >
                <FaInfoCircle /> Hero
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "categories" ? "active" : ""}`}
                onClick={() => setActiveTab("categories")}
              >
                <FaList /> Categories
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "venues" ? "active" : ""}`}
                onClick={() => setActiveTab("venues")}
              >
                <FaStar /> Venues
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "testimonials" ? "active" : ""}`}
                onClick={() => setActiveTab("testimonials")}
              >
                <FaQuoteLeft /> Testimonials
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "images" ? "active" : ""}`}
                onClick={() => setActiveTab("images")}
              >
                <FaImage /> Images
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "footer" ? "active" : ""}`}
                onClick={() => setActiveTab("footer")}
              >
                <FaGlobe /> Footer
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "contact" ? "active" : ""}`}
                onClick={() => setActiveTab("contact")}
              >
                <FaPhone /> Contact
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "social" ? "active" : ""}`}
                onClick={() => setActiveTab("social")}
              >
                <FaFacebook /> Social
              </button>
            </div>
          </div>

          <div className="settings-content">{renderTabContent()}</div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="spinning" /> Saving...
              </>
            ) : (
              <>
                <FaSave /> Save Settings
              </>
            )}
          </button>
        </div>
      </form>
   </div>
    </div>
  );
};

export default SettingsForm;